import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass } from '@fortawesome/free-solid-svg-icons';
import {
  refreshTokenBlackoutWindowSeconds,
  useAlertContext,
  useTimeout,
  useUserContext,
} from '../common';
import { signout } from '.';
import { compose as lcompose } from 'lodash/fp';
import { map as emap, right, left } from 'fp-ts/Either';
import { flow } from 'fp-ts/function';

export function useRefreshTokenReminder({
  lastUserActionTime,
  refreshToken,
}: {
  lastUserActionTime: number;
  refreshToken: () => void;
}) {
  const {
    user: { refreshTokenReminderDelay, accessTokenExpirationDelay },
    setUser,
  } = useUserContext();

  const [timedoutAt, setTimedoutAt] = useState<number>();

  const { resetTimeout } = useTimeout(
    () => {
      setTimedoutAt(Date.now());
    },
    refreshTokenReminderDelay,
    'reminder'
  );

  const refreshTokenActiveWindowSeconds =
    accessTokenExpirationDelay / 1000 - 120; // okta token exp - 2 minutes;

  useEffect(() => {
    lcompose(
      emap(lcompose(() => setTimedoutAt(void 0), refreshToken, resetTimeout)),
      ({
        timedoutAt,
        lastUserActionTime,
      }: {
        timedoutAt: number | undefined;
        lastUserActionTime: number;
      }) =>
        timedoutAt &&
        0 <= (timedoutAt - lastUserActionTime) / 1000 &&
        (timedoutAt - lastUserActionTime) / 1000 <=
          refreshTokenActiveWindowSeconds
          ? right(void 0)
          : left(void 0)
    )({ timedoutAt, lastUserActionTime });
  }, [
    timedoutAt,
    lastUserActionTime,
    refreshTokenActiveWindowSeconds,
    refreshToken,
    resetTimeout,
  ]);

  const { setAlert } = useAlertContext();

  useEffect(() => {
    lcompose(
      emap(() =>
        setAlert(a => ({
          ...a,
          title: 'CSOS Session Timeout',
          message: () => (
            <>
              <FontAwesomeIcon icon={faHourglass} size="1x" fixedWidth spin />{' '}
              Your session will expire in {refreshTokenBlackoutWindowSeconds}{' '}
              seconds. Please click OK to continue.
            </>
          ),
          okAction: true,
          cancelAction: true,
          isOpen: true,
          onOk: lcompose(
            () => setTimedoutAt(void 0),
            refreshToken,
            resetTimeout
          ),
          onCancel: flow(() => setTimedoutAt(void 0), signout(setUser)),
          cancelLabel: 'Log Out',
        }))
      ),
      ({
        timedoutAt,
        lastUserActionTime,
      }: {
        timedoutAt: number | undefined;
        lastUserActionTime: number;
      }) =>
        timedoutAt &&
        (0 > (timedoutAt - lastUserActionTime) / 1000 ||
          (timedoutAt - lastUserActionTime) / 1000 >
            refreshTokenActiveWindowSeconds)
          ? right(void 0)
          : left(void 0)
    )({ timedoutAt, lastUserActionTime });
  }, [
    timedoutAt,
    lastUserActionTime,
    refreshTokenActiveWindowSeconds,
    refreshToken,
    resetTimeout,
    setAlert,
    setUser,
  ]);
}
